import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { State } from '../../../modules/reviewer/profile/general/general.model';
import { ReviewerService } from '../../services/reviewer.service';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';
import { lastValueFrom } from 'rxjs';
import { Reviewer } from '../../../store/reviewer/reviewer.interface';
import { constants } from '../../constants/constants';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { VerificationCodeComponent } from '../verification-code/verification-code.component';
import { CommonBaseClass } from '../../classes/common-base-class';
import { HelperService } from '../../services/helper.service';
import moment from 'moment';
import { environment } from '../../../../environments/environment';

export class ProfileInformationClass extends CommonBaseClass {
  profileForm!: FormGroup;
  stateDropDown!: State[];
  isPhoneVerified: boolean = false;
  reviewerInfo!: Reviewer;
  userFirmId!: string;
  currentTenant: any = null; 
  citizenshipType = constants.citizenshipType;
  emailBtnText = 'Change Email';
  maxDate = moment().subtract(16, 'years').toDate();
  allowEditFields = {
    ssn: false,
    dob: false,
    firstName: false,
    lastName: false
  };

  constructor(
    protected fb: FormBuilder,
    protected reviewerService: ReviewerService,
    protected toastService: ToastService,
    protected userService: UserService,
    protected router: Router,
    protected reviewerDetailRef: DynamicDialogRef,
    protected dialogService: DialogService,
    helperService: HelperService
  ) {
    super(helperService);
    this.stateDropDown = constants.states;
  }

  async init(): Promise<void> {
    this.isPWCEnabled = environment.isPWCEnabled;
    this.initializeForm();
    await this.getReviewerInfo();
    this.checkAllowFields();
    this.getTenant();
    this.updateValidators();
    this.isPhoneVerified = this.reviewerInfo?.profile?.contact?.isPhoneVerified;
    this.populateData();
  }

  onSubmit(): void {
    if (this.isPhoneVerified) {
      if (this.profileForm.valid) {
        this.updateReviewer();
      }
    } else {
      this.toastService.showError('Please verify phone number');
    }
  }

  onDiscard(): void {
    this.router.navigate(['/reviewer/dashboard'])
  }

  updateReviewer(): void {
    this.reviewerInfo.profile.searchMarkets = this.reviewerInfo.profile.searchMarkets.map((e: any) => e._id.toString());
    const payload: Reviewer = {
      ...this.reviewerInfo,
      profile: {
        ...this.reviewerInfo.profile,
        contact: {
          ...this.profileForm.get('profile.contact')?.value,
          state: this.profileForm.get('profile.contact.state')?.value?.name,
          isPhoneVerified: this.isPhoneVerified,
          home: this.profileForm.get('profile.contact.home')?.value.replace(/-/g, ''),
        },
        immigrationStatus: this.profileForm.get('profile.immigrationStatus')?.value?.value,
      },
      firstName: this.profileForm.get('firstName')?.value,
      lastName: this.profileForm.get('lastName')?.value,
      preferredFirstName: this.profileForm.get('preferredFirstName')?.value,
    };
    if(this.allowEditFields.dob){
      payload.profile.dob = moment(this.profileForm.get('profile.dob')?.value).format('YYYY-MM-DD');
    }
    this.reviewerService.updateReviewer(payload).subscribe({
      next: (res) => {
        this.toastService.showSuccess('Updated successfully');
        this.getReviewerInfo();
        this.reviewerService.fetchReviewerAndUpdate();
      },
    });
  }

  initializeForm(): void {
    this.profileForm = this.fb.group({
      profile: this.fb.group({
        contact: this.fb.group({
          street: [''],
          city: ['', Validators.required],
          state: [''],
          zipCode: ['', [Validators.required,Validators.pattern(constants.zipCodeRegex)]],
          mobile: [''],
          home: ['', Validators.required],
          isPhoneVerified: [false],
        }),
        dob: [''],
        ssn: [''],
        immigrationStatus:[''],
      }),
      firstName: [{value: '', disabled: !this.allowEditFields.firstName}, Validators.required],
      lastName: [{value: '', disabled: !this.allowEditFields.lastName}, Validators.required],
      preferredFirstName: [''],
      email: ['', [Validators.required, Validators.pattern(constants.emailRegex)]],
    });
  }

  async sendVerificationCode(): Promise<void> {
    this.reviewerService
      .sendVerificationPhoneSms(
        this.profileForm.get('profile.contact.home')?.value.replace(/-/g, ''),
      )
      .subscribe({
        next: async (res) => {
          if (res.message === 'Phone number successfully verified') {
            this.isPhoneVerified = true;
            this.disablePhoneField();
          } else {
            this.toastService.showSuccess(res.message);
          }
        },
      });
  }

  verify(code: string): void {
    if(code == ''){
      this.toastService.showError('Please enter verification code');
      return;
    }
    const body = {
      code: code,
      _id: this.reviewerInfo._id,
      phoneNo: this.profileForm.get('profile.contact.home')?.value.replace(/-/g, ''),
    };
    this.userService.verifyPhone(body).subscribe({
      next: () => {
        this.isPhoneVerified = true;
        this.disablePhoneField();
        this.toastService.showSuccess('Phone number verified');
      }
    });
  }

  disablePhoneField(): void {
    if (this.isPhoneVerified) {
      (this.profileForm.get('profile.contact.home') as FormControl).disable();
    }
  }

  enablePhoneField(): void {
    if (this.isPhoneVerified) {
      (this.profileForm.get('profile.contact.home') as FormControl).enable();
      this.isPhoneVerified = false;
    }
  }

  changeEmail(){
    if(this.emailBtnText === 'Change Email'){
      (this.profileForm.get('email') as FormControl).enable();
      this.emailBtnText = 'Verify';
    }
    else if(this.emailBtnText == 'Verify'){

      if(!(this.profileForm.get('email') as FormControl).valid){
        this.toastService.showError('Invalid email');
        return;
      }
  
      this.reviewerService.sendVerificationCodeAgainstEmail(this.profileForm.get('email')?.value).subscribe((res) => {
        this.toastService.showSuccess(res.message);

        this.reviewerDetailRef = this.dialogService.open(VerificationCodeComponent, {
          data: {
            userId: this.reviewerInfo._id,
            email: this.profileForm.get('email')?.value
          },
          header: 'Verification Code',
          width: '30vw',
          contentStyle: { overflow: 'auto' },
          breakpoints: {
            '960px': '75vw',
            '640px': '90vw'
          }
        });
  
        this.reviewerDetailRef.onClose.subscribe((data: any) => {
          if (data) {
            (this.profileForm.get('email') as FormControl).disable();
            this.toastService.showSuccess('Email address updated successfully');
            this.emailBtnText = 'Change Email';
            this.getReviewerInfo();
          }
        });
      });
    }
  }

  async getReviewerInfo(): Promise<void> {
    this.reviewerInfo = await lastValueFrom(
      this.reviewerService.fetchReviewer()
    );
  }

  getTenant(): void {
    this.currentTenant = this.reviewerService.getSelectedTenant();
    this.userFirmId = this.currentTenant?.firm?._id || '';
  }

  updateValidators(): void{
    const immigrationStatusControl = this.profileForm.get('profile.immigrationStatus');
    if(!this.currentTenant?.firm.isBackgroundCheckRequired && this.currentTenant?.firm.isCitizenshipStatusRequired){
      immigrationStatusControl?.setValidators([Validators.required]);
    } else{
      immigrationStatusControl?.clearValidators();
    }
    this.profileForm.get('immigrationStatus')?.updateValueAndValidity({ emitEvent: false });

    const streetControl = this.profileForm.get('profile.contact.street');
    if (this.isPWCEnabled) {
      streetControl?.clearValidators();
    } else {
      streetControl?.setValidators([Validators.required]);
    }
    streetControl?.updateValueAndValidity({ emitEvent: false });
  }

  setFormValues(field: string, value: any): void {
    (this.profileForm.get(field) as FormControl).setValue(value);
  }

  populateData(): void {
    this.setFormValues('firstName', this.reviewerInfo?.firstName);
    this.setFormValues('lastName', this.reviewerInfo?.lastName);
    this.setFormValues('preferredFirstName', this.reviewerInfo.preferredFirstName || '');
    this.setFormValues('email', this.reviewerInfo?.email);
    this.setFormValues(
      'profile.contact.home',
      this.reviewerInfo?.profile?.contact?.home
    );
    this.setFormValues(
      'profile.contact.street',
      this.reviewerInfo?.profile?.contact?.street
    );
    this.setFormValues(
      'profile.contact.city',
      this.reviewerInfo?.profile?.contact?.city
    );
    this.setFormValues(
      'profile.contact.zipCode',
      this.reviewerInfo?.profile?.contact?.zipCode
    );
    this.setFormValues(
      'profile.contact.state',
      this.stateDropDown.find(
        (state) => state.name === this.reviewerInfo?.profile?.contact?.state
      )
    );
    this.setFormValues(
      'profile.immigrationStatus',
      this.citizenshipType.find(
        (status) => status.name === this.reviewerInfo.profile?.immigrationStatus
      )
    );
    (this.profileForm.get('email') as FormControl).disable();
    this.disablePhoneField();
  }

  checkAllowFields() {
    this.reviewerService.getAllowedFieldsForModification(this.reviewerInfo._id).subscribe((res) => {
      if (res && (res.ssn || res.dob || res.firstName || res.lastName)) {
        this.allowEditFields = res;
        if(this.allowEditFields.ssn){
           this.profileForm.get('profile.ssn')?.setValidators([Validators.required]);
        }
        if(this.allowEditFields.dob){
          const dob = moment.tz(this.reviewerInfo?.profile?.dob, 'UTC').format("MM/DD/YYYY");
          this.setFormValues('profile.dob', dob);
          this.profileForm.get('profile.dob')?.setValidators([Validators.required]);
        }
        if(this.allowEditFields.firstName){
          (this.profileForm.get('firstName') as FormControl).enable();
        }
        if(this.allowEditFields.lastName){
          (this.profileForm.get('lastName') as FormControl).enable();
        }
      }
    });
  }

  formatSSN(event: any): void {
    event.target.value = this.helperService.formatSSN(event.target.value);
  }
}
